/**
 * 🎓 VoucherRedeemModal — 学生兑换算力券（教育版专用）
 *
 * 教育版没有充值入口，学生拿到的是老师发的券码，在这里兑换成积分。
 *
 * ⚠️ 界面上不出现任何钱的字样：不是「购买」、不是「支付」，只是「兑换」。
 *    券由学校免费发放，兑换得到的是算力（积分）。
 */

import React, { useState } from 'react';
import { X, Ticket, Loader2, CheckCircle } from 'lucide-react';
import api from '../api';

export default function VoucherRedeemModal({ open, onClose, onSuccess }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { credits, balance }

  if (!open) return null;

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('请输入算力券码');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/vouchers/redeem', { code: trimmed });
      setDone({ credits: res.credits, balance: res.balance });
      // 立刻刷新顶部积分，别让学生以为没到账。
      // 传后端返回的余额而不是「原值 + credits」：并发下前端那份可能已经过时了。
      if (onSuccess) onSuccess(res.balance);
    } catch (err) {
      // 后端把「不存在/已兑换/已过期」都做成了可读的 400 提示，直接展示
      setError(err.message || '兑换失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError('');
    setDone(null);
    onClose();
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.8)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 400, background: 'var(--bg-panel)', borderRadius: 16,
          border: '1px solid var(--border-subtle)', overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Ticket size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>
                兑换算力券
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                输入老师发放的券码，兑换成积分
              </p>
            </div>
          </div>
          <button onClick={handleClose} style={{
            background: 'transparent', border: 'none', padding: 8,
            cursor: 'pointer', color: 'var(--text-muted)'
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <CheckCircle size={44} color="#34d399" style={{ marginBottom: 12 }} />
              <p style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
                兑换成功 +{done.credits} 积分
              </p>
              <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--text-muted)' }}>
                当前余额 {done.balance} 积分
              </p>
              <button onClick={handleClose} style={{
                width: '100%', padding: '12px', borderRadius: 8,
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer'
              }}>
                知道了
              </button>
            </div>
          ) : (
            <>
              <input
                autoFocus
                type="text"
                value={code}
                placeholder="例如 V-ABCD234XYZ"
                onChange={e => { setCode(e.target.value); setError(''); }}
                onKeyDown={e => { if (e.key === 'Enter' && !loading) handleRedeem(); }}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '13px 14px',
                  borderRadius: 8, background: 'var(--modal-input-bg)',
                  border: `1px solid ${error ? 'rgba(234,179,8,0.5)' : 'var(--border-subtle)'}`,
                  color: 'var(--text-primary)', fontSize: 16, outline: 'none',
                  fontFamily: 'monospace', letterSpacing: 1, textTransform: 'uppercase'
                }}
              />
              <p style={{ margin: '8px 0 16px', fontSize: 12, minHeight: 17, color: error ? '#eab308' : 'var(--text-muted)' }}>
                {error || '不区分大小写，前面的 V- 可以不输'}
              </p>
              <button
                onClick={handleRedeem}
                disabled={loading}
                style={{
                  width: '100%', padding: '12px', borderRadius: 8,
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none', color: '#fff', fontSize: 14, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                {loading ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> 兑换中...</>
                ) : '兑换'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
